import React, { FunctionComponent, ReactElement } from "react";
import {
  GameCodes,
  GameTitles,
  Point,
  RoundHistoryComponentProps,
  RoundType,
} from "../../type";
import {
  MatchedLine,
  HunkCaiShenGameData,
  SpinHistory,
  FESymbols,
  Symbols,
} from "./type";
import HunkCaiShenUtils from "./utils";
import "./hunk-cai-shen.scss";
import { Link } from "react-router-dom";
import Utils from "../../utils";
import { BuyFreeSpinRoundHistoryView } from "../../components";
import { useFocusSpinId } from "../../hooks";
import BigNumber from "bignumber.js";
import { Multiply } from "../gold-rush-bonanza/type";
import { useTranslator } from "../../context/TranslationContext";

const lineRatio = BigNumber(0.2);
export const HunkCaiShenRoundHistoryView: FunctionComponent<
  RoundHistoryComponentProps
> = ({ type, id, roundHistory }) => {
  const focusSpinId = useFocusSpinId(type, id);
  const translator = useTranslator();
  const spin =
    roundHistory.spins.find((s) => s.id === focusSpinId) ??
    roundHistory.spins[0];

  if (!spin) return <>Spin not found</>;

  if (!spin.focus) spin.focus = true;

  const plays = roundHistory.spins.reduce((ps, s) => {
    if (!ps[s.playRefNumber]) ps[s.playRefNumber] = [];
    ps[s.playRefNumber].push(s);
    return ps;
  }, {} as { [k: string]: SpinHistory[] });

  return (
    <BuyFreeSpinRoundHistoryView
      gameCode={GameCodes.HunkCaiShen}
      roundHistory={roundHistory}
    >
      <div className="card mb-3">
        <div className="card-body">
          <div className="card-title fs-4 fw-bold">
            {translator.get("spin")}
            <button
              type="button"
              className="btn btn-info float-end"
              data-bs-toggle="modal"
              data-bs-target="#explaination"
            >
              <i className="bi bi-info-circle fw-bold"></i>
            </button>
          </div>

          <hr />
          {Object.values(plays).map((s) => (
            <PlayRecord
              key={s[0].playRefNumber}
              spins={s}
              betAmount={BigNumber(+roundHistory.betAmount)}
              lineAmount={BigNumber(+roundHistory.lineAmount)}
              onSpinClicked={(spinId) => {
                spin.focus = false;
              }}
              buyFreeSpinRate={BigNumber(
                roundHistory.buyFreeSpin ? roundHistory.buyFreeSpinRate : 1
              )}
              roundType={roundHistory.roundType}
            />
          ))}
        </div>
      </div>
      <div id="explaination" className={`modal fade`} tabIndex={-1}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{translator.get("explanation")}</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="pb-3 fs-5">{translator.get("formula")}</div>
              <div className="pb-3">
                {translator.get("bet_size")} = {translator.get("bet")} x 0.2
              </div>
              <div className="pb-3">
                {translator.get("payout")} = {translator.get("bet_size")} x{" "}
                {translator.get("multiplier")}
              </div>
              <hr />
              <div className="pb-3 fs-5">{translator.get("multiplier")}</div>
              <div className="d-flex justify-content-evenly mb-2">
                {[
                  Symbols.Wild3,
                  Symbols.Wild4,
                  Symbols.Wild5,
                  Symbols.Scatter,
                ].map(HunkCaiShenUtils.explainationSymbolPayout)}
              </div>
              <div className="d-flex justify-content-evenly mb-2">
                {[
                  Symbols.Symbol1,
                  Symbols.Symbol2,
                  Symbols.Symbol3,
                  Symbols.Symbol4,
                  Symbols.Symbol5,
                  Symbols.Symbol6,
                ].map(HunkCaiShenUtils.explainationSymbolPayout)}
              </div>
              <div className="d-flex justify-content-evenly mb-2">
                {[
                  Symbols.LowSymbol1,
                  Symbols.LowSymbol2,
                  Symbols.LowSymbol3,
                  Symbols.LowSymbol4,
                ].map(HunkCaiShenUtils.explainationSymbolPayout)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </BuyFreeSpinRoundHistoryView>
  );
};
const PlayRecord: FunctionComponent<{
  spins: SpinHistory[];
  betAmount: BigNumber;
  lineAmount: BigNumber;
  onSpinClicked: (id: string) => void;
  buyFreeSpinRate: BigNumber;
  roundType?: RoundType;
}> = ({
  spins,
  betAmount,
  lineAmount,
  onSpinClicked,
  buyFreeSpinRate,
  roundType,
}) => {
  const translator = useTranslator();
  const firstSpin = spins[0];
  const validSpins = spins.filter((s) => s.gameData);
  const payout = spins.reduce((t, s) => t.plus(+s.winAmount), BigNumber(0));
  const elements = [
    <div
      key={`play-${firstSpin.playRefNumber}-id`}
      className="list-group-item list-group-item-primary text-center border fs-4"
    >
      {firstSpin.mode === "Base"
        ? translator.get("normal_spin_id")
        : translator.get("free_spin_id")}
      : <span className="fw-bold">{firstSpin.playRefNumber}</span>
    </div>,
    <div
      key={`play-${firstSpin.playRefNumber}-header`}
      className="list-group-item list-group-item-secondary spin-table"
    >
      <div className="d-flex text-center">
        <div className="col-4 border">{translator.get("spin_count")}</div>
        <div className="col-2 border">{translator.get("bet_size")}</div>
        <div className="col-3 border">{translator.get("payout")}</div>
        <div className="col-3 border">{translator.get("profit")}</div>
      </div>
    </div>,
    <div
      key={`play-${firstSpin.playRefNumber}-content`}
      className="list-group-item text-center border border"
    >
      <div className="d-flex">
        <div className="col-4 border">{validSpins.length}</div>
        <div className="col-2 border">
          {Utils.asCurrency(
            betAmount.multipliedBy(lineRatio).dividedBy(buyFreeSpinRate),
            2
          )}
        </div>
        <div className="col-3 border">{Utils.asCurrency(payout)}</div>
        <div className="col-3 border">
          {Utils.asCurrency(
            firstSpin.reelMatrix.length > 1 &&
              firstSpin.mode === "Base" &&
              roundType !== RoundType.FreePlay
              ? payout.minus(betAmount).dividedBy(buyFreeSpinRate)
              : payout,
            2
          )}
        </div>
      </div>
    </div>,
  ] as ReactElement[];

  validSpins.forEach((s) => {
    elements.push(
      <SpinRecord
        key={s.id}
        spin={s}
        betAmount={betAmount}
        lineAmount={lineAmount}
        onSpinClicked={onSpinClicked}
      />
    );
  });
  return <div className="mb-2">{elements}</div>;
};

export const SpinRecord: FunctionComponent<{
  betAmount: BigNumber;
  lineAmount: BigNumber;
  spin: SpinHistory;
  onSpinClicked: (id: string) => void;
}> = ({ lineAmount, betAmount, spin, onSpinClicked }) => {
  const translator = useTranslator();
  const gameData = (spin.gameData as HunkCaiShenGameData) ?? {};
  if (!gameData) return <></>;
  const animationGroups = gameData.animationGroup ?? ({} as MatchedLine[]);
  const header = (
    <>
      <colgroup>
        <col width="15%" />
        <col width="30%" />
        <col width="20%" />
        <col width="15%" />
        <col width="15%" />
      </colgroup>

      <thead>
        <tr className="text-center list-group-item-secondary">
          <td className="">{translator.get("symbol")}</td>
          <td className="">{translator.get("line")}</td>
          <td className="">{translator.get("rate")}</td>
          <td className="">{translator.get("bonus")}</td>
          <td className="">{translator.get("payout")}</td>
        </tr>
      </thead>
    </>
  );

  let payoutTableRows: ReactElement[] = [];
  if (gameData.scatterMultiplier) {
    payoutTableRows.push(
      <tr key={`payout-${spin.id}-scatter`} className="fw-bold fs-6">
        <td>
          <div className="symbol flex">
            {HunkCaiShenUtils.convertSymbol(Symbols.Scatter)}
          </div>
        </td>
        <td></td>
        <td>
          {translator.get("bet")} x{gameData.scatterMultiplier}
        </td>
        <td>-</td>
        <td>
          {Utils.asCurrency(betAmount.multipliedBy(gameData.scatterMultiplier))}
        </td>
      </tr>
    );
  }
  if (gameData.scatterPoints.length >= 3) {
    payoutTableRows.push(
      <tr key={`payout-${spin.id}-freespin`}>
        <td className="px-4" colSpan={999}>
          {translator.get("bonus")}
        </td>
      </tr>
    );
  }
  if (Object.keys(animationGroups).length > 0) {
    payoutTableRows.push(
      ...Object.entries(animationGroups).map((p, i) => {
        const [key, line] = p;
        const { lineDraw, symbolId } = HunkCaiShenUtils.convertLine(
          spin.reelMatrix,
          +key,
          line
        );
        const symbolPayout = Utils.changeFraction(
          BigNumber(line.beforeWin).dividedBy(lineAmount)
        );
        const bonusPayout =
          line.win === line.beforeWin
            ? "-"
            : "x" +
              Utils.changeFraction(
                BigNumber(line.win).dividedBy(line.beforeWin)
              );

        return (
          <tr key={`payout-${spin.id}-${i}`}>
            <td>
              <div className="symbol flex">
                {HunkCaiShenUtils.convertSymbol(symbolId)}
              </div>
            </td>
            <td>{lineDraw}</td>
            <td>x{symbolPayout}</td>
            <td>{bonusPayout}</td>
            <td>{Utils.asCurrency(p[1].win)}</td>
          </tr>
        );
      })
    );
  }

  if (payoutTableRows.length > 0) {
    payoutTableRows.push(
      <tr key={`payout-${spin.id}-hr`}>
        <td className="px-4" colSpan={999}>
          <hr />
        </td>
      </tr>
    );
    payoutTableRows.push(
      <tr key={`payout-${spin.id}-total`} className="fw-bold fs-6">
        <td>{translator.get("win")}:</td>
        <td colSpan={3}></td>
        <td>{Utils.asCurrency(+spin.winAmount, 2)}</td>
      </tr>
    );
  }

  let content = (
    <div className="d-flex flex-wrap text-center ">
      <div className="col-12 col-md-6  py-3">
        <Reel
          reelMatrix={spin.reelMatrix}
          points={gameData.animationGroup || {}}
          multiplys={gameData.multiplys}
          scatters={gameData.scatterPoints}
        />
      </div>
      <div
        className={`col-12 col-md-6 pb-3 ${
          spin.focus ? " list-group-item-success" : ""
        }`}
      >
        <table width={"100%"}>
          {header}
          <tbody>{payoutTableRows}</tbody>
        </table>
      </div>
    </div>
  );
  // if (!spin.reelMatrix || spin.reelMatrix.length === 0) {
  //   content = (
  //     <div className={`col-12 pb-3 text-center`}>
  //       <table width={"100%"}>
  //         <tbody>{payoutTableRows}</tbody>
  //       </table>
  //     </div>
  //   );
  // }

  return (
    <Link
      to={{ hash: spin.id }}
      id={spin.id}
      type="button"
      onClick={() => onSpinClicked(spin.id)}
      key={`spin-${spin.id}-id`}
      className={`list-group-item list-group-item-action border${
        spin.focus ? " border-success" : ""
      }`}
    >
      {content}
    </Link>
  );
};

export const Reel: FunctionComponent<{
  reelMatrix?: number[][];
  points: { [k: number]: MatchedLine };
  multiplys: Multiply[];
  scatters?: Point[];
}> = ({ reelMatrix = [], points, multiplys, scatters = [] }) => {
  const matchedSet = new Set(
    Object.values(points).flatMap((m) => m.points.map((p) => `${p.x}-${p.y}`))
  );
  scatters.forEach((p) => {
    matchedSet.add(`${p.x}-${p.y}`);
  });

  const reelColumn = reelMatrix.reduce((col, rawCol, x) => {
    const colElements = rawCol.reduce((row, rawSymbol, y) => {
      const key = `${x}-${y}`;
      const matched = matchedSet.has(key);
      const symbol =
        rawSymbol === FESymbols.Wild
          ? HunkCaiShenUtils.takeWildRealId(x, y, multiplys)
          : rawSymbol;

      row.push(<BoardCell key={key} symbol={symbol} matched={matched} />);
      return row;
    }, [] as ReactElement[]);
    col.push(
      <div key={"col-" + x} className="d-flex flex-column">
        {colElements}
      </div>
    );
    return col;
  }, [] as ReactElement[]);

  return <div className="reel d-flex justify-content-center">{reelColumn}</div>;
};

const BoardCell: FunctionComponent<{
  symbol: number;
  matched: boolean;
}> = ({ symbol, matched }) => {
  const classes = ["symbol", "flex", "border", "border-dark"];
  let cellContent = HunkCaiShenUtils.convertSymbol(symbol);

  if (matched) {
    classes.push("animation");
  }

  return <div className={classes.join(" ")}>{cellContent}</div>;
};
